import React, { useState, useEffect } from 'react';
import { getWeatherIconClass } from '../utils/weatherIconMapper';
import 'weather-icons/css/weather-icons.min.css';

const WeatherForecast = ({ forecast }) => {
  const [expandedDay, setExpandedDay] = useState(null);
  
  // Enhanced logging to debug weather conditions and icon mappings
  useEffect(() => {
    console.log('forecast', forecast);
    if (forecast && forecast.length > 0) {
      console.log('Weather forecast conditions:', forecast.map(day => ({
        dayOfWeek: day.dayOfWeek,
        condition: day.condition,
        isDay: day.isDay,
        iconClass: getWeatherIconClass(day.condition, day.isDay)
      })));
    }
  }, [forecast]);
  
  // Log the first forecast day to debug missing data
  useEffect(() => {
    if (forecast && forecast.length > 0) {
      console.log('First forecast day data:', {
        complete: forecast[0],
        precipitation: forecast[0].precipitation,
        precipChance: forecast[0].precipChance,
        precipData: forecast[0].precipData,
        rawData: forecast[0].rawData,
        details: forecast[0].details,
      });
    }
  }, [forecast]);
  
  // Helper function to render temperature values safely
  const renderTemperature = (temp) => {
    if (temp === undefined || temp === null || temp === '--') return '--';
    
    // Check if temperature is a simple number
    if (typeof temp === 'number') {
      return `${temp}°`;
    }
    
    // Check if temperature is an object with high/low properties
    if (typeof temp === 'object') {
      if (temp.high !== undefined) {
        return `${temp.high}°`;
      }
      if (temp.low !== undefined) {
        return `${temp.low}°`;
      }
    }
    
    // Last resort: convert to string
    return `${temp}`;
  };
  
  // Calculate width percentage based on temperature range (high - low)
  const calculateTempRangeWidth = (low, high) => {
    const minWidthPercent = 40; // Tighter min/max range
    const maxWidthPercent = 70;
    const maxTempRange = 60; // Keep assumed max range

    if (low === undefined || low === null || low === '--' || 
        high === undefined || high === null || high === '--') {
      return `${(minWidthPercent + maxWidthPercent) / 2}%`; // Default average width
    }
    
    const lowTempNum = parseFloat(low);
    const highTempNum = parseFloat(high);

    if (isNaN(lowTempNum) || isNaN(highTempNum) || highTempNum <= lowTempNum) {
      return `${minWidthPercent}%`; // Minimum width if range is invalid or zero
    }

    const range = highTempNum - lowTempNum;
    const widthPercent = (range / maxTempRange) * 100;
    
    // Clamp the width between min and max percentages
    const clampedWidth = Math.max(minWidthPercent, Math.min(maxWidthPercent, widthPercent));
    
    return `${clampedWidth}%`;
  };
  
  // Format date for display with proper day name
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Get actual day of week
  const getDayOfWeek = (index, dayName) => {
    // Simplify: Always calculate the short day name based on index
    const today = new Date();
    const date = new Date(today);
    date.setDate(today.getDate() + index);
    // Return short weekday name
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };
  
  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '--';
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return timeString;
    }
  };
  
  // Toggle expanded state for a day
  const toggleDayExpand = (index) => {
    if (expandedDay === index) {
      setExpandedDay(null); // Collapse if already expanded
    } else {
      setExpandedDay(index); // Expand the clicked day
    }
  };

  return (
    <div className="tab-content upcoming-tab">
      <table className="seven-day-forecast-table">
        <thead>
          <tr>
            <th>DAY</th>
            <th></th>
            <th>PRECIP</th>
            <th>TEMP</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {forecast.map((day, index) => {
            // Extract temperature values safely
            const lowTemp = typeof day.temperature === 'object' ? day.temperature.low : null;
            const highTemp = typeof day.temperature === 'object' ? day.temperature.high : day.temperature;
            
            const isExpanded = expandedDay === index;
            const weatherIconClass = getWeatherIconClass(day.condition, day.isDay);
            
            // Get the actual day name
            const actualDayName = getDayOfWeek(index, day.dayOfWeek);
            
            return (
              <React.Fragment key={index}>
                <tr 
                  className={isExpanded ? 'expanded-row' : ''}
                  onClick={() => toggleDayExpand(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="day-name">{actualDayName}</td>
                  <td className="weather-icon">
                    <div className="icon-container">
                      <i className={`wi ${weatherIconClass} weather-icon`}></i>
                    </div>
                  </td>
                  <td className="precip">
                    <i className="wi wi-raindrop"></i>
                    {day.precipitation}
                  </td>
                  <td className="temp-range">
                    <div className="temp-bar">
                      {/* Slider with dynamic width */}
                      <div 
                        className="temp-slider"
                        style={{ width: calculateTempRangeWidth(lowTemp, highTemp) }}
                      >
                        {/* Temps moved back inside slider */}
                        <div className="low-temp">
                          {lowTemp !== null && lowTemp !== undefined ? `${lowTemp}°` : ''}
                        </div>
                        <div className="high-temp">
                          {renderTemperature(highTemp)}
                        </div>
                      </div>
                      {/* Remove temps from outside 
                      <div className="low-temp">
                        {lowTemp !== null && lowTemp !== undefined ? `${lowTemp}°` : ''}
                      </div>
                      <div className="high-temp">
                        {renderTemperature(highTemp)}
                      </div>
                      */} 
                    </div>
                  </td>
                  {/* Add a separate element for mobile view */}
                  <td className="temp-range-mobile">
                    <div className="temp-text-mobile">
                      {lowTemp !== null && lowTemp !== undefined ? `${lowTemp}°` : '--'} /{' '}
                      {renderTemperature(highTemp)}
                    </div>
                  </td>
                  <td className="detail-btn">
                    <button 
                      className={`detail-circle ${isExpanded ? 'expanded' : ''}`}
                      aria-label="More details" 
                      title={day.condition || 'Weather conditions'}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click from triggering
                        toggleDayExpand(index);
                      }}
                    >
                      <i className={`wi ${isExpanded ? 'wi-direction-down' : 'wi-direction-right'}`}></i>
                      <span className="sr-only">{isExpanded ? 'Hide details' : 'Show details'}</span>
                    </button>
                  </td>
                </tr>
                
                {isExpanded && (
                  <tr className="details-row">
                    <td colSpan="5">
                      <div className="day-details">
                        {/* <div className="day-details-header">
                          <h3>{actualDayName.toUpperCase()}</h3>
                        </div> */}
                        
                        <div className="day-details-content">
                          <div className="day-details-grid">
                            {/* Left Column - Day Summary */}
                            <div className="day-summary">
                              <div className="day-icon">
                                <i className={`wi ${weatherIconClass} weather-icon large-icon`}></i>
                              </div>
                              <div className="day-temps">
                                <div className="day-high-temp">{renderTemperature(day.temperature.high)}</div>
                                <div className="day-low-temp">/{renderTemperature(day.temperature.low)}</div>
                              </div>
                              <div className="day-narrative">
                                {/* Use the full narrative from the data */} 
                                {day.narrative || `${day.condition}. Highs in the upper ${day.temperature.high}s and lows in the upper ${day.temperature.low}s.`}
                              </div>
                            </div>
                            
                            {/* Right Column - Weather Details */}
                            <div className="weather-details-list">
                              <div className="details-two-column">
                                <div className="details-column">
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">Chance of Rain:</div>
                                    <div className="detail-item-value">
                                      {/* Use day-specific chance if available, else overall precip */}
                                      {day.details?.day?.precipChance !== undefined ? `${day.details.day.precipChance}%` : day.precipitation}
                                    </div>
                                  </div>
                                  
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">Sunset:</div>
                                    <div className="detail-item-value">
                                      <i className="wi wi-sunset"></i> {formatTime(day.details?.sunset)}
                                    </div>
                                  </div>
                                  
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">Humidity:</div>
                                    <div className="detail-item-value">
                                      {/* Use data from details.day */}
                                      {day.details?.day?.relativeHumidity !== undefined ? `${day.details.day.relativeHumidity}%` : 'N/A'}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="details-column">
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">Wind:</div>
                                    <div className="detail-item-value">
                                      {/* Use data from details.day */}
                                      {day.details?.day?.windDirectionCardinal || '-'} @ {day.details?.day?.windSpeed || '-'} mph
                                    </div>
                                  </div>
                                  
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">Sunrise:</div>
                                    <div className="detail-item-value">
                                      <i className="wi wi-sunrise"></i> {formatTime(day.details?.sunrise)}
                                    </div>
                                  </div>
                                  
                                  <div className="detail-item-row">
                                    <div className="detail-item-label">UV Index:</div>
                                    <div className="detail-item-value">
                                      {/* Use data from details.day */}
                                      {day.details?.day?.uvIndex !== undefined ? `${day.details.day.uvIndex} ${day.details?.day?.uvDescription || ''}` : 'N/A'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Night Forecast Section */}
                          {day.details?.night && (
                            <div className="night-forecast-section">
                              <div className="night-title">
                                <i className="wi wi-night-clear"></i> {actualDayName} Night
                              </div>
                              <div className="night-description">
                                {/* Use data from details.night */}
                                {day.details.night.narrative || day.details.night.wxPhraseLong || 'Night details unavailable.'}
                                {day.details.night.temperature && ` Low ${day.details.night.temperature}°F.`}
                                {day.details.night.windPhrase && ` ${day.details.night.windPhrase}.`}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default WeatherForecast; 